import { trace } from "@opentelemetry/api";
import LogRocket from "logrocket";
import type { OrgChartNode, OrgChartNodeOrAssistant } from "@domains/org-chart/shared/orgChartSchemas.ts";
import { getOrgChartAssistants } from "./getOrgChartAssistants.ts";

const TRACER_NAME = "@modules/organization";
const DURATION_PROPERTY = "app.org_chart.duration_ms";

const ORG_CHART_RENDER_FAILED_EVENT = { logRocketName: "Org Chart Render Failed", spanName: "org-chart-render-failed" } as const;
export const ORG_CHART_RENDER_COMPLETE_EVENT = { logRocketName: "Org Chart Render Complete", spanName: "org-chart-render" } as const;

export type OrgChartSizeBucket = "<50" | "50-200" | "200-500" | "500+";

export interface OrgChartSize {
    nodeCount: number;
    depth: number;
    sizeBucket: OrgChartSizeBucket;
}

export type OrgChartTelemetryAttributes = Readonly<Record<string, string | number | boolean>>;

export interface OrgChartTelemetryEvent {
    logRocketName: string;
    spanName: string;
    startTime: number;
    endTime: number;
    attributes: OrgChartTelemetryAttributes;
}

export function getOrgChartSizeBucket(nodeCount: number): OrgChartSizeBucket {
    if (nodeCount < 50) {
        return "<50";
    }

    if (nodeCount < 200) {
        return "50-200";
    }

    return nodeCount < 500 ? "200-500" : "500+";
}

export function measureOrgChart(roots: readonly OrgChartNode[]): OrgChartSize {
    let nodeCount = 0;
    let depth = 0;
    const pending: { node: OrgChartNodeOrAssistant; level: number }[] = roots.map(node => ({ node, level: 1 }));

    for (let entry = pending.pop(); entry; entry = pending.pop()) {
        nodeCount++;
        depth = Math.max(depth, entry.level);

        if ("children" in entry.node) {
            for (const child of entry.node.children) {
                pending.push({ node: child, level: entry.level + 1 });
            }
        }

        for (const assistant of getOrgChartAssistants(entry.node)) {
            pending.push({ node: assistant, level: entry.level + 1 });
        }
    }

    return { nodeCount, depth, sizeBucket: getOrgChartSizeBucket(nodeCount) };
}

export function getOrgChartSizeAttributes({ nodeCount, depth, sizeBucket }: OrgChartSize): OrgChartTelemetryAttributes {
    return { "app.org_chart.node_count": nodeCount, "app.org_chart.depth": depth, "app.org_chart.size_bucket": sizeBucket };
}

export function reportOrgChartEvent({ logRocketName, spanName, startTime, endTime, attributes }: OrgChartTelemetryEvent) {
    LogRocket.track(logRocketName, { ...attributes, [DURATION_PROPERTY]: Math.round(endTime - startTime) });

    const span = trace.getTracer(TRACER_NAME).startSpan(spanName, { startTime: performance.timeOrigin + startTime, attributes });

    span.end(performance.timeOrigin + endTime);
}

export function reportOrgChartRenderFailed(error: Error, openPage: string | undefined) {
    const attributes = { "app.org_chart.error_name": error.name, ...(openPage && { "app.org_chart.route": openPage }) };

    LogRocket.track(ORG_CHART_RENDER_FAILED_EVENT.logRocketName, attributes);

    const span = trace.getTracer(TRACER_NAME).startSpan(ORG_CHART_RENDER_FAILED_EVENT.spanName, { attributes });

    span.recordException(error);
    span.end();
}
